import { useEffect, useState } from 'react';

import { paginate } from '@/helpers';

interface IProps<T> {
  data: T[];
  pageSize: number;
}

interface IPaginationOptions {
  value: number;
  label: string;
}

interface IUsePagination<T> {
  handlePrev: () => void;
  handleNext: () => void;
  page: number;
  paginatedData: T[][];
  paginationOptions: IPaginationOptions;
}

const usePagination = <T>({ data, pageSize }: IProps<T>): IUsePagination<T> => {
  const [page, setPage] = useState(0);
  const [paginatedData, setPaginatedData] = useState<IUsePagination<T>['paginatedData']>([[]]);
  const [paginationOptions, setPaginationOptions] = useState<IUsePagination<T>['paginationOptions']>({
    value: 0,
    label: '',
  });

  useEffect(() => {
    setPaginatedData(paginate(data, pageSize));
  }, [data, pageSize]);

  useEffect(() => {
    setPaginationOptions({ value: 0, label: `${page + 1}/${paginatedData.length || 1}` });
  }, [page, paginatedData.length]);

  const handlePrev = () => page > 0 && setPage(page - 1);
  const handleNext = () => page < paginatedData.length - 1 && setPage(page + 1);

  return {
    handlePrev,
    handleNext,
    page,
    paginatedData,
    paginationOptions,
  };
};

export { usePagination };
